import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { API, getCached, setCached } from "./cache";
import "./DailyTimesheetView.css";

const STATUS_OPTS   = ["Draft", "Submitted", "Approved"];
const REF_TYPE_OPTS = ["project", "task", "feature", "sub_feature", "dependency", "prerequisite", "defect", "meeting"];
const REF_TYPE_LABELS = {
  project:        "Projects",
  task: "Tasks",
  feature:        "Module",
  sub_feature:    "Sub Module",
  dependency:     "Dependency",
  prerequisite:   "Pre-requisite",
  defect:         "Defect",
  meeting:        "Meeting",
};

const REF_TYPE_COLORS = {
  project:        { bg: "#e8eaf6", color: "#1a237e" },
  task: { bg: "#e8f5e9", color: "#2e7d32" },
  feature:        { bg: "#e3f2fd", color: "#1565c0" },
  sub_feature:    { bg: "#f3e5f5", color: "#6a1b9a" },
  dependency:     { bg: "#fff8e1", color: "#f57f17" },
  prerequisite:   { bg: "#fce4ec", color: "#c62828" },
  defect:         { bg: "#ffebee", color: "#b71c1c" },
  meeting:        { bg: "#e0f2f1", color: "#00695c" },
};

const STATUS_STYLE = {
  Draft:     { bg: "#fff8e1", color: "#f57f17" },
  Submitted: { bg: "#e3f2fd", color: "#1565c0" },
  Approved:  { bg: "#e8f5e9", color: "#2e7d32" },
};

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d + "T00:00:00").toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function fmtTime(h, m) {
  const hrs  = h || 0;
  const mins = m || 0;
  if (hrs === 0 && mins === 0) return "—";
  if (hrs === 0)  return `${mins}m`;
  if (mins === 0) return `${hrs}h`;
  return `${hrs}h ${mins}m`;
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function currentMonthStart() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function currentMonthEnd() {
  const d = new Date();
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, "0")}-${String(last.getDate()).padStart(2, "0")}`;
}

function validateTime(hours, minutes, showToast) {
  const h = Number(hours);
  const m = Number(minutes);
  if (h < 0 || h > 8)  { showToast("Hours must be between 0 and 8", "warn"); return false; }
  if (m < 0 || m > 59) { showToast("Minutes must be between 0 and 59", "warn"); return false; }
  return true;
}

function validateSideDrawerTimeEntry(hours, minutes) {
  const h = Number(hours);
  const m = Number(minutes);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return { ok: false, msg: "Enter a valid time value", level: "warn" };
  if (h < 0 || h > 24) return { ok: false, msg: "Hours must be between 0 and 24", level: "warn" };
  if (m < 0 || m > 59) return { ok: false, msg: "Minutes must be between 0 and 59", level: "warn" };

  const totalMinutes = h * 60 + m;
  if (totalMinutes > 240) {
    return {
      ok: false,
      kind: "hard_limit",
      msg: "Only upto 4 hours, time log is allowed. Split the entry into two or more, if you want to enter more than 4 hours",
      level: "warn",
    };
  }
  if (totalMinutes > 120) {
    return {
      ok: false,
      kind: "confirm_over_2h",
      msg: "You are entering more than 2 hours, check if it is valid",
      level: "warn",
    };
  }
  return { ok: true };
}

const EMPTY_CREATE = { report_date: today(), ref_type: "", ref_id: "", title: "", overall_notes: "", hours_spent: 0, minutes_spent: 0, status: "Draft" };

/* ─────────────────────────────────────────────
   Ref Entity Autocomplete
   Searches across projects, tasks, features
   Calls onChange(ref_id, ref_type) on selection
───────────────────────────────────────────── */
function RefEntityAutocomplete({ refId, refType, entities, loading, onChange, required = false }) {
  const toLabel = useCallback((id, type) => {
    if (!id) return "";
    const e = entities.find(e => e.id === id && e.ref_type === type);
    return e ? e.label : id.slice(0, 8) + "…";
  }, [entities]);

  const [query,     setQuery]     = useState(() => toLabel(refId, refType));
  const [open,      setOpen]      = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => { setQuery(toLabel(refId, refType)); }, [refId, refType, toLabel]);
  useEffect(() => { setActiveIdx(-1); }, [query]);
  useEffect(() => {
    if (activeIdx >= 0 && listRef.current) {
      listRef.current.children[activeIdx]?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIdx]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q
      ? entities.filter(e =>
          e.label.toLowerCase().includes(q) ||
          e.sublabel?.toLowerCase().includes(q) ||
          e.sub_feature?.toLowerCase().includes(q) ||
          e.description?.toLowerCase().includes(q) ||
          e.ref_type.toLowerCase().includes(q)
        )
      : entities;
    return base.slice(0, 60);
  }, [query, entities]);

  const select = (item) => {
    setQuery(item.label);
    onChange(item.id, item.ref_type);
    setOpen(false);
  };

  const clear = () => {
    setQuery("");
    onChange("", "");
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if      (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx(p => Math.min(p + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp")   { e.preventDefault(); setActiveIdx(p => Math.max(p - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIdx >= 0 && filtered[activeIdx]) select(filtered[activeIdx]);
      else if (filtered.length === 1) select(filtered[0]);
    }
    else if (e.key === "Escape") { setOpen(false); }
  };

  const isEmpty = !refId;

  return (
    <div className="dts-ac-wrap" ref={wrapRef}>
      <div className={`dts-ac-input-row${required && isEmpty ? " dts-ac-input-row--required" : ""}`}>
        <input
          className="dts-ac-input"
          value={query}
          placeholder={loading ? "Loading entities…" : "Search project / feature…"}
          disabled={loading}
          onFocus={() => setOpen(true)}
          onChange={e => { setQuery(e.target.value); setOpen(true); if (!e.target.value) onChange("", ""); }}
          onKeyDown={handleKeyDown}
          onBlur={e => { if (wrapRef.current?.contains(e.relatedTarget)) return; setOpen(false); }}
        />
        {refId && (
          <button className="dts-ac-clear" onMouseDown={e => { e.preventDefault(); clear(); }} title="Clear">✕</button>
        )}
      </div>

      {/* Selected entity pill */}
      {refId && (
        <div className="dts-ac-pill">
          <span className="dts-ac-pill-type">{refType}</span>
          <span className="dts-ac-pill-id">{refId}</span>
        </div>
      )}

      {/* Dropdown */}
      {open && !loading && (
        <div className="dts-ac-dropdown" ref={listRef}>
          {filtered.length === 0 && (
            <div className="dts-ac-empty">No results for "{query}"</div>
          )}
          {filtered.map((item, i) => (
            <div
              key={`${item.ref_type}-${item.id}`}
              className={`dts-ac-option${i === activeIdx ? " dts-ac-option--active" : ""}`}
              tabIndex={-1}
              onMouseDown={e => { e.preventDefault(); select(item); }}
            >
              <span className="dts-ac-opt-type">{item.ref_type}</span>
              <div className="dts-ac-opt-text">
                <span className="dts-ac-opt-label">{item.label}</span>
                {item.sublabel && <span className="dts-ac-opt-sub">{item.sublabel}</span>}
                {item.sub_feature && <span className="dts-ac-opt-sub">↳ {item.sub_feature}</span>}
                {item.description && <span className="dts-ac-opt-desc">{item.description}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DailyTimesheetView({ showToast, user }) {
  const [reports,       setReports]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [filterDateFrom, setFilterDateFrom] = useState(currentMonthStart);
  const [filterDateTo,   setFilterDateTo]   = useState(currentMonthEnd);
  const [filterStatus,  setFilterStatus]  = useState("");
  const [filterRefType, setFilterRefType] = useState("");

  // Detail panel
  const [selectedReport, setSelectedReport] = useState(null);
  const [detailLoading,  setDetailLoading]  = useState(false);

  // Header editing
  const [editingHeader, setEditingHeader] = useState(false);
  const [headerDraft,   setHeaderDraft]   = useState({});
  const [savingHeader,  setSavingHeader]  = useState(false);

  // Create modal
  const [showCreate,  setShowCreate]  = useState(false);
  const [createForm,  setCreateForm]  = useState(EMPTY_CREATE);
  const [creating,    setCreating]    = useState(false);

  // Ref entity autocomplete data
  const [entities,        setEntities]        = useState([]);
  const [entitiesLoading, setEntitiesLoading] = useState(true);

  /* ── Load ref entities (projects + tasks + features + dependencies) ── */
  const loadEntities = useCallback(async () => {
    setEntitiesLoading(true);
    try {
      const cachedCP = getCached("customers-projects-active");

      const [mf, cp, ft, dr, sf, df] = await Promise.all([
        // Tasks, features, sub-features, dependencies, and defects always fetched fresh
        fetch(`${API}/tasks`, { credentials: "include" }).then(r => r.json()).catch(() => []),
        cachedCP ? Promise.resolve(cachedCP)
          : fetch(`${API}/customers-projects?active_only=true`, { credentials: "include" }).then(r => r.json()).catch(() => []),
        fetch(`${API}/features`, { credentials: "include" }).then(r => r.json()).catch(() => []),
        fetch(`${API}/dependency-requests`, { credentials: "include" }).then(r => r.json()).catch(() => []),
        fetch(`${API}/sub-features`, { credentials: "include" }).then(r => r.json()).catch(() => []),
        fetch(`${API}/defects?limit=500`, { credentials: "include" }).then(r => r.json()).catch(() => []),
      ]);

      if (!cachedCP && Array.isArray(cp) && cp.length > 0) setCached("customers-projects-active", cp);

      const unified = [];

      // Projects from customers-projects hierarchy
      if (Array.isArray(cp)) {
        cp.forEach(customer => {
          (customer.projects || []).forEach(project => {
            if (project.id) {
              unified.push({
                id:       project.id,
                ref_type: "project",
                label:    project.project_name,
                sublabel: customer.customer_name,
              });
            }
          });
        });
      }

      // Master features
      if (Array.isArray(mf)) {
        mf.forEach(f => {
          if (f.id) {
            unified.push({
              id:          f.id,
              ref_type:    "task",
              label:       f.feature || "—",
              sublabel:    [f.customer, f.project].filter(Boolean).join(" / "),
              sub_feature: f.sub_feature || "",
              description: f.description || "",
            });
          }
        });
      }

      // Features
      if (Array.isArray(ft)) {
        ft.forEach(f => {
          if (f.id) {
            unified.push({
              id:       f.id,
              ref_type: "feature",
              label:    f.feature_name || f.name || "—",
              sublabel: f.feature_code || "",
            });
          }
        });
      }

      // Dependencies
      if (Array.isArray(dr)) {
        dr.forEach(d => {
          if (d.id) {
            unified.push({
              id:           d.id,
              ref_type:     "dependency",
              label:        d.title || "Untitled dependency",
              sublabel:     [d.dependency_type, d.status].filter(Boolean).join(" · "),
              project_name: d.project_name || d.customer_name || null,
            });
          }
        });
      }

      // Sub-features
      if (Array.isArray(sf)) {
        sf.forEach(s => {
          if (s.id) {
            unified.push({
              id:       s.id,
              ref_type: "sub_feature",
              label:    s.sub_feature_name || "—",
              sublabel: s.sub_feature_code || "",
            });
          }
        });
      }

      // Defects
      if (Array.isArray(df)) {
        df.forEach(d => {
          if (d.id) {
            unified.push({
              id:           d.id,
              ref_type:     "defect",
              label:        [d.defect_code, d.title].filter(Boolean).join(" — "),
              sublabel:     [d.project_name, d.status].filter(Boolean).join(" · "),
              project_name: d.project_name || null,
            });
          }
        });
      }

      setEntities(unified);
    } catch { /* non-critical — autocomplete degrades gracefully */ }
    setEntitiesLoading(false);
  }, []);

  useEffect(() => { loadEntities(); }, [loadEntities]);

  /* ── Load reports list ── */
  const loadReports = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterDateFrom)       params.set("date_from", filterDateFrom);
    if (filterDateTo)         params.set("date_to",   filterDateTo);
    if (filterStatus)         params.set("status",     filterStatus);
    if (filterRefType)        params.set("ref_type",   filterRefType);
    if (user?.db_user_id)     params.set("user_id",    user.db_user_id);
    try {
      const r = await fetch(`${API}/daily-reports?${params}`, { credentials: "include" });
      if (!r.ok) throw new Error();
      const data = await r.json();
      setReports(Array.isArray(data) ? data : []);
    } catch {
      showToast("Failed to load reports", "warn");
    }
    setLoading(false);
  }, [filterDateFrom, filterDateTo, filterStatus, filterRefType, user?.db_user_id, showToast]);

  useEffect(() => { loadReports(); }, [loadReports]);

  /* ── Admin consolidated stats (all users, same filters) ── */
  const isAdmin = ["Admin", "Super Admin"].includes(user?.db_role);
  const [adminStats,        setAdminStats]        = useState(null);
  const [adminStatsLoading, setAdminStatsLoading] = useState(false);

  const loadAdminStats = useCallback(async () => {
    if (!isAdmin) return;
    setAdminStatsLoading(true);
    const params = new URLSearchParams();
    if (filterDateFrom) params.set("date_from", filterDateFrom);
    if (filterDateTo)   params.set("date_to",   filterDateTo);
    if (filterStatus)   params.set("status",    filterStatus);
    if (filterRefType)  params.set("ref_type",  filterRefType);
    params.set("limit", "2000");
    try {
      const r = await fetch(`${API}/daily-reports?${params}`, { credentials: "include" });
      if (!r.ok) throw new Error();
      const data = await r.json();
      const rows = Array.isArray(data) ? data : [];
      let totalMins = 0, taskMins = 0, defectMins = 0;
      rows.forEach(rpt => {
        const mins = (rpt.hours_spent || 0) * 60 + (rpt.minutes_spent || 0);
        totalMins += mins;
        if (rpt.ref_type === "task")   taskMins   += mins;
        if (rpt.ref_type === "defect") defectMins += mins;
      });
      const toHM = m => ({ hours: Math.floor(m / 60), minutes: m % 60 });
      setAdminStats({ total: toHM(totalMins), tasks: toHM(taskMins), defects: toHM(defectMins) });
    } catch {
      setAdminStats(null);
    }
    setAdminStatsLoading(false);
  }, [isAdmin, filterDateFrom, filterDateTo, filterStatus, filterRefType]);

  useEffect(() => { loadAdminStats(); }, [loadAdminStats]);

  /* ── Open report detail ── */
  const openReport = async (id) => {
    setDetailLoading(true);
    setSelectedReport(null);
    setEditingHeader(false);
    try {
      const r = await fetch(`${API}/daily-reports/${id}`, { credentials: "include" });
      if (!r.ok) throw new Error();
      setSelectedReport(await r.json());
    } catch {
      showToast("Failed to load report", "warn");
    }
    setDetailLoading(false);
  };

  /* ── Create report ── */
  const createReport = async () => {
    if (!createForm.ref_type || !createForm.ref_id) { showToast("A reference entity is required", "warn"); return; }
    if (!createForm.title.trim()) { showToast("Title is required", "warn"); return; }
    if (!validateTime(createForm.hours_spent, createForm.minutes_spent, showToast)) return;
    setCreating(true);
    try {
      const body = {
        ...createForm,
        hours_spent:   Number(createForm.hours_spent),
        minutes_spent: Number(createForm.minutes_spent),
      };
      if (!body.report_date) delete body.report_date;
      if (!body.ref_type) delete body.ref_type;
      if (!body.ref_id)   delete body.ref_id;
      const r = await fetch(`${API}/daily-reports`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error();
      const newRpt = await r.json();
      showToast("Report created");
      setShowCreate(false);
      setCreateForm(EMPTY_CREATE);
      await loadReports();
      await openReport(newRpt.id);
    } catch {
      showToast("Failed to create report", "warn");
    }
    setCreating(false);
  };

  /* ── Save report header ── */
  const saveHeader = async () => {
    if (!headerDraft.ref_type || !headerDraft.ref_id) { showToast("A reference entity is required", "warn"); return; }
    if (!validateTime(headerDraft.hours_spent, headerDraft.minutes_spent, showToast)) return;
    setSavingHeader(true);
    try {
      const payload = {
        ...headerDraft,
        hours_spent:   Number(headerDraft.hours_spent),
        minutes_spent: Number(headerDraft.minutes_spent),
      };
      if (!payload.ref_type) delete payload.ref_type;
      if (!payload.ref_id)   delete payload.ref_id;
      const r = await fetch(`${API}/daily-reports/${selectedReport.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error();
      setSelectedReport(prev => ({ ...prev, ...payload }));
      setReports(prev => prev.map(rpt => rpt.id === selectedReport.id ? { ...rpt, ...payload } : rpt));
      setEditingHeader(false);
      showToast("Report updated");
    } catch {
      showToast("Failed to update report", "warn");
    }
    setSavingHeader(false);
  };

  /* ── Submit draft report ── */
  const submitReport = async (id, e) => {
    e.stopPropagation();
    try {
      const r = await fetch(`${API}/daily-reports/${id}/status`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "Submitted" }),
      });
      if (!r.ok) throw new Error();
      showToast("Report submitted");
      setReports(prev => prev.map(rpt => rpt.id === id ? { ...rpt, status: "Submitted" } : rpt));
      if (selectedReport?.id === id) setSelectedReport(prev => ({ ...prev, status: "Submitted" }));
    } catch {
      showToast("Failed to submit report", "warn");
    }
  };

  /* ── Delete report ── */
  const deleteReport = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this daily report?")) return;
    try {
      const r = await fetch(`${API}/daily-reports/${id}`, { method: "DELETE", credentials: "include" });
      if (!r.ok) throw new Error();
      showToast("Report deleted");
      setReports(prev => prev.filter(rpt => rpt.id !== id));
      if (selectedReport?.id === id) setSelectedReport(null);
    } catch {
      showToast("Failed to delete report", "warn");
    }
  };

  /* ── Helpers ── */
  const startEditHeader = () => {
    setHeaderDraft({
      report_date:   selectedReport.report_date,
      ref_type:      selectedReport.ref_type      ?? "",
      ref_id:        selectedReport.ref_id        ?? "",
      title:         selectedReport.title,
      overall_notes: selectedReport.overall_notes,
      hours_spent:   selectedReport.hours_spent   ?? 0,
      minutes_spent: selectedReport.minutes_spent ?? 0,
      status:        selectedReport.status,
    });
    setEditingHeader(true);
  };


  const TYPE_DISPLAY = { task: "task" };

  // resolved_project_name is now returned by the API directly.
  // Fall back to local entity lookup for types not yet resolved server-side.
  const getProjectLabel = useCallback((rpt) => {
    if (rpt.resolved_project_name) return rpt.resolved_project_name;
    const { ref_id: refId, ref_type: refType } = rpt;
    if (!refId) return null;
    const entity = entities.find(e => e.id === refId && e.ref_type === refType);
    if (!entity) return null;
    if (refType === "project") return entity.label;
    if (refType === "task") {
      const parts = (entity.sublabel || "").split(" / ");
      return parts.length > 1 ? parts[parts.length - 1] : entity.sublabel || null;
    }
    if (refType === "dependency") return entity.project_name || null;
    if (refType === "defect")     return entity.project_name || null;
    return null;
  }, [entities]);

  /* ── Time summary ── */
  const timeSummary = useMemo(() => {
    let totalMins = 0;
    const byType = {};
    reports.forEach(rpt => {
      const mins = (rpt.hours_spent || 0) * 60 + (rpt.minutes_spent || 0);
      totalMins += mins;
      if (rpt.ref_type) {
        byType[rpt.ref_type] = (byType[rpt.ref_type] || 0) + mins;
      }
    });
    const toHM = (m) => ({ hours: Math.floor(m / 60), minutes: m % 60 });
    return {
      total: toHM(totalMins),
      byType: Object.entries(byType)
        .sort((a, b) => b[1] - a[1])
        .map(([type, mins]) => ({ type, ...toHM(mins) })),
    };
  }, [reports]);

  /* ── Column resize ── */
  const [colWidths, setColWidths] = useState({
    date:    110,
    title:   260,
    reftype:  90,
    project: 220,
    user:    180,
    time:     90,
    status:  110,
  });
  const startResize = useCallback((e, col) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = colWidths[col];

    const onMove = (ev) => {
      const delta = ev.clientX - startX;
      setColWidths(prev => ({ ...prev, [col]: Math.max(60, startW + delta) }));
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, [colWidths]);

  /* ─────────────────── RENDER ─────────────────── */
  return (
    <div className="dts-wrap">

      {/* Toolbar */}
      <div className="dts-toolbar">
        <input
          type="date"
          className="dts-filter-input"
          value={filterDateFrom}
          max={filterDateTo || undefined}
          onChange={e => setFilterDateFrom(e.target.value)}
          title="From date"
        />
        <span style={{ color: "#aaa", fontSize: 12 }}>–</span>
        <input
          type="date"
          className="dts-filter-input"
          value={filterDateTo}
          min={filterDateFrom || undefined}
          onChange={e => setFilterDateTo(e.target.value)}
          title="To date"
        />
        <select
          className="dts-filter-select"
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
        >
          <option value="">All Statuses</option>
          {STATUS_OPTS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          className="dts-filter-select"
          value={filterRefType}
          onChange={e => setFilterRefType(e.target.value)}
        >
          <option value="">All Types</option>
          {REF_TYPE_OPTS.map(t => <option key={t} value={t}>{REF_TYPE_LABELS[t]}</option>)}
        </select>
        {(filterDateFrom || filterDateTo || filterStatus || filterRefType) && (
          <button className="dts-clear-btn" onClick={() => { setFilterDateFrom(""); setFilterDateTo(""); setFilterStatus(""); setFilterRefType(""); }}>
            Clear filters
          </button>
        )}
        <span className="dts-toolbar__spacer" />
        <span className="dts-count">{reports.length} report{reports.length !== 1 ? "s" : ""}</span>
        <button className="toolbar__add-btn" onClick={() => setShowCreate(true)}>+ New Entry</button>
      </div>

      {/* Admin consolidated cards */}
      {isAdmin && (
        <div className="dts-admin-stats">
          <div className="dts-admin-stats__label">All Users — Consolidated</div>
          <div className="dts-admin-stats__cards">
            <div className="dts-admin-card dts-admin-card--total">
              <div className="dts-admin-card__title">Total Time Spent</div>
              <div className="dts-admin-card__value">
                {adminStatsLoading ? "…" : adminStats ? fmtTime(adminStats.total.hours, adminStats.total.minutes) : "—"}
              </div>
            </div>
            <div className="dts-admin-card dts-admin-card--tasks">
              <div className="dts-admin-card__title">Total Time on Tasks</div>
              <div className="dts-admin-card__value">
                {adminStatsLoading ? "…" : adminStats ? fmtTime(adminStats.tasks.hours, adminStats.tasks.minutes) : "—"}
              </div>
            </div>
            <div className="dts-admin-card dts-admin-card--defects">
              <div className="dts-admin-card__title">Total Time on Defects</div>
              <div className="dts-admin-card__value">
                {adminStatsLoading ? "…" : adminStats ? fmtTime(adminStats.defects.hours, adminStats.defects.minutes) : "—"}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Time summary cards */}
      {!loading && reports.length > 0 && (
        <div className="status-overview">
          <div className="status-overview__title">Your Time Log</div>
          <div className="status-overview__grid">
            <div className="status-overview__item">
              <div className="status-overview__badge">Total Time</div>
              <div className="status-overview__count">{fmtTime(timeSummary.total.hours, timeSummary.total.minutes)}</div>
            </div>
            {timeSummary.byType.map(({ type, hours, minutes }) => (
              <div key={type} className="status-overview__item">
                <div className="status-overview__badge">{REF_TYPE_LABELS[type] || type}</div>
                <div className="status-overview__count">{fmtTime(hours, minutes)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main layout */}
      <div className="dts-content">

        {/* Reports list */}
        <div className="dts-list">
          <div className="dts-list-inner">
            <div className="dts-list-header">
              <div className="dts-th" style={{ flex: `1 1 ${colWidths.date}px`, minWidth: 60, overflow: "hidden" }}>
                Date
                <div className="dts-col-resize" onMouseDown={e => startResize(e, "date")} />
              </div>
              <div className="dts-th" style={{ flex: `2 1 ${colWidths.title}px`, minWidth: 80, overflow: "hidden" }}>
                Title
                <div className="dts-col-resize" onMouseDown={e => startResize(e, "title")} />
              </div>
              <div className="dts-th" style={{ flex: `1 1 ${colWidths.reftype}px`, minWidth: 60, overflow: "hidden" }}>
                Type
                <div className="dts-col-resize" onMouseDown={e => startResize(e, "reftype")} />
              </div>
              <div className="dts-th" style={{ flex: `2 1 ${colWidths.project}px`, minWidth: 80, overflow: "hidden" }}>
                Project
                <div className="dts-col-resize" onMouseDown={e => startResize(e, "project")} />
              </div>
              <div className="dts-th" style={{ flex: `1 1 ${colWidths.user}px`, minWidth: 80, overflow: "hidden" }}>
                Submitted By
                <div className="dts-col-resize" onMouseDown={e => startResize(e, "user")} />
              </div>
              <div className="dts-th" style={{ flex: `1 1 ${colWidths.time}px`, minWidth: 60, overflow: "hidden" }}>
                Time
                <div className="dts-col-resize" onMouseDown={e => startResize(e, "time")} />
              </div>
              <div className="dts-th" style={{ flex: `1 1 ${colWidths.status}px`, minWidth: 60, overflow: "hidden" }}>
                Status
                <div className="dts-col-resize" onMouseDown={e => startResize(e, "status")} />
              </div>
              <div className="dts-th dts-th--action" />
            </div>

            {loading && <div className="dts-state">Loading…</div>}
            {!loading && reports.length === 0 && (
              <div className="dts-state">No reports found. Create one to get started.</div>
            )}

            {!loading && reports.map(rpt => (
              <div
                key={rpt.id}
                className={`dts-row${selectedReport?.id === rpt.id ? " dts-row--selected" : ""}`}
                onClick={() => openReport(rpt.id)}
              >
                <div className="dts-td" style={{ flex: `1 1 ${colWidths.date}px`, minWidth: 60, overflow: "hidden" }}>{fmtDate(rpt.report_date)}</div>
                <div className="dts-td dts-td--title-cell" style={{ flex: `2 1 ${colWidths.title}px`, minWidth: 80, overflow: "hidden" }}>
                  {rpt.title || <span className="dts-empty">Untitled</span>}
                </div>
                <div className="dts-td" style={{ flex: `1 1 ${colWidths.reftype}px`, minWidth: 60, overflow: "hidden" }}>
                  {rpt.ref_type
                    ? <span className="dts-type-tag">{TYPE_DISPLAY[rpt.ref_type] || rpt.ref_type}</span>
                    : <span className="dts-empty">—</span>}
                </div>
                <div className="dts-td" style={{ flex: `2 1 ${colWidths.project}px`, minWidth: 80, overflow: "hidden" }}>
                  {getProjectLabel(rpt) || <span className="dts-empty">—</span>}
                </div>
                <div className="dts-td" style={{ flex: `1 1 ${colWidths.user}px`, minWidth: 80, overflow: "hidden" }}>
                  {rpt.user_display_name || rpt.user_name || <span className="dts-empty">—</span>}
                </div>
                <div className="dts-td dts-td--time-cell" style={{ flex: `1 1 ${colWidths.time}px`, minWidth: 60, overflow: "hidden" }}>{fmtTime(rpt.hours_spent, rpt.minutes_spent)}</div>
                <div className="dts-td" style={{ flex: `1 1 ${colWidths.status}px`, minWidth: 60, overflow: "hidden" }}>
                  <span
                    className="dts-badge"
                    style={{ background: STATUS_STYLE[rpt.status]?.bg, color: STATUS_STYLE[rpt.status]?.color }}
                  >
                    {rpt.status}
                  </span>
                </div>
                <div className="dts-td dts-td--action">
                  {rpt.status === "Draft" && (
                    <button className="dts-submit-btn" onClick={e => submitReport(rpt.id, e)} title="Submit report">Submit</button>
                  )}
                  <button className="dts-del-btn" onClick={e => deleteReport(rpt.id, e)} title="Delete report">✕</button>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* Detail modal */}
      {(selectedReport || detailLoading) && (
        <div className="dts-overlay">
          <div className="dts-detail-modal" onClick={e => e.stopPropagation()}>
            {detailLoading && <div className="dts-state">Loading…</div>}

            {selectedReport && !detailLoading && (
              <>
                {/* Report header */}
                <div className="dts-detail-header">
                  <div className="dts-detail-header-top">
                    <div className="dts-detail-meta-row">
                      <span className="dts-detail-date">{fmtDate(selectedReport.report_date)}</span>
                      {selectedReport.ref_type && (
                        <span className="dts-detail-project">{selectedReport.ref_type}</span>
                      )}
                    </div>
                    <div className="dts-detail-actions">
                      <span
                        className="dts-badge"
                        style={{ background: STATUS_STYLE[selectedReport.status]?.bg, color: STATUS_STYLE[selectedReport.status]?.color }}
                      >
                        {selectedReport.status}
                      </span>
                      {!editingHeader && (
                        <button className="uv-btn uv-btn--edit" onClick={startEditHeader}>Edit</button>
                      )}
                      <button className="dts-close-btn" onClick={() => { setSelectedReport(null); setEditingHeader(false); }} title="Close">✕</button>
                    </div>
                  </div>

                  {!editingHeader ? (
                    <>
                      <div className="dts-detail-title">{selectedReport.title || <span className="dts-empty">Untitled</span>}</div>
                      {selectedReport.overall_notes && (
                        <div className="dts-detail-notes">{selectedReport.overall_notes}</div>
                      )}
                      <div className="dts-detail-time-row">
                        <span className="dts-detail-time-label">Total time:</span>
                        <span className="dts-detail-time-val">{fmtTime(selectedReport.hours_spent, selectedReport.minutes_spent)}</span>
                      </div>
                      <div className="dts-detail-by">
                        by {selectedReport.user_display_name || selectedReport.user_name || "—"}
                      </div>
                    </>
                  ) : (
                    <div className="dts-edit-form">
                      <div className="dts-field">
                        <label className="dts-label">Date</label>
                        <input
                          type="date" className="dts-input"
                          value={headerDraft.report_date ?? ""}
                          onChange={e => setHeaderDraft(d => ({ ...d, report_date: e.target.value }))}
                        />
                      </div>
                      <div className="dts-field">
                        <label className="dts-label">Title</label>
                        <input
                          className="dts-input"
                          placeholder="Report title"
                          value={headerDraft.title ?? ""}
                          onChange={e => setHeaderDraft(d => ({ ...d, title: e.target.value }))}
                        />
                      </div>
                      <div className="dts-field">
                        <label className="dts-label">Overall Notes</label>
                        <textarea
                          className="dts-textarea"
                          rows={3}
                          placeholder="Summary of today's work…"
                          value={headerDraft.overall_notes ?? ""}
                          onChange={e => setHeaderDraft(d => ({ ...d, overall_notes: e.target.value }))}
                        />
                      </div>
                      <div className="dts-edit-form-row">
                        <div className="dts-field">
                          <label className="dts-label">Hours Spent</label>
                          <input
                            type="number" className="dts-input"
                            min={0} max={8}
                            value={headerDraft.hours_spent ?? 0}
                            onChange={e => setHeaderDraft(d => ({ ...d, hours_spent: e.target.value }))}
                          />
                        </div>
                        <div className="dts-field">
                          <label className="dts-label">Minutes Spent</label>
                          <input
                            type="number" className="dts-input"
                            min={0} max={59}
                            value={headerDraft.minutes_spent ?? 0}
                            onChange={e => setHeaderDraft(d => ({ ...d, minutes_spent: e.target.value }))}
                          />
                        </div>
                      </div>
                      <div className="dts-field">
                        <label className="dts-label">Reference Entity *</label>
                        <RefEntityAutocomplete
                          refId={headerDraft.ref_id ?? ""}
                          refType={headerDraft.ref_type ?? ""}
                          entities={entities}
                          loading={entitiesLoading}
                          required
                          onChange={(id, type) => setHeaderDraft(d => ({ ...d, ref_id: id, ref_type: type }))}
                        />
                      </div>
                      <div className="dts-field">
                        <label className="dts-label">Status</label>
                        <select
                          className="dts-select"
                          value={headerDraft.status ?? "Draft"}
                          onChange={e => setHeaderDraft(d => ({ ...d, status: e.target.value }))}
                        >
                          {STATUS_OPTS.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div className="dts-form-actions">
                        <button className="uv-btn uv-btn--save" onClick={saveHeader} disabled={savingHeader}>
                          {savingHeader ? "…" : "Save"}
                        </button>
                        <button className="uv-btn uv-btn--cancel" onClick={() => setEditingHeader(false)} disabled={savingHeader}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>

              </>
            )}
          </div>
        </div>
      )}

      {/* Create Report Modal */}
      {showCreate && (
        <div className="dts-overlay" onClick={() => setShowCreate(false)}>
          <div className="dts-modal" onClick={e => e.stopPropagation()}>
            <div className="dts-modal-header">
              <span className="dts-modal-title">New Daily Report</span>
              <button className="dts-close-btn" onClick={() => setShowCreate(false)}>✕</button>
            </div>
            <div className="dts-modal-body">
              <div className="dts-field">
                <label className="dts-label">Date</label>
                <input
                  type="date"
                  className="dts-input"
                  value={createForm.report_date}
                  onChange={e => setCreateForm(d => ({ ...d, report_date: e.target.value }))}
                />
              </div>
              <div className="dts-field">
                <label className="dts-label">Reference Entity *</label>
                <RefEntityAutocomplete
                  refId={createForm.ref_id}
                  refType={createForm.ref_type}
                  entities={entities}
                  loading={entitiesLoading}
                  required
                  onChange={(id, type) => setCreateForm(d => ({ ...d, ref_id: id, ref_type: type }))}
                />
              </div>
              <div className="dts-field">
                <label className="dts-label">Title *</label>
                <input
                  className="dts-input"
                  placeholder="e.g. Sprint 12 Day 3"
                  value={createForm.title}
                  onChange={e => setCreateForm(d => ({ ...d, title: e.target.value }))}
                  onKeyDown={e => e.key === "Enter" && createReport()}
                  autoFocus
                />
              </div>
              <div className="dts-field">
                <label className="dts-label">Overall Notes</label>
                <textarea
                  className="dts-textarea"
                  rows={3}
                  placeholder="Summary of today's work…"
                  value={createForm.overall_notes}
                  onChange={e => setCreateForm(d => ({ ...d, overall_notes: e.target.value }))}
                />
              </div>
              <div className="dts-modal-row">
                <div className="dts-field">
                  <label className="dts-label">Hours Spent</label>
                  <input
                    type="number" className="dts-input"
                    min={0} max={8}
                    value={createForm.hours_spent}
                    onChange={e => setCreateForm(d => ({ ...d, hours_spent: e.target.value }))}
                  />
                </div>
                <div className="dts-field">
                  <label className="dts-label">Minutes Spent</label>
                  <input
                    type="number" className="dts-input"
                    min={0} max={59}
                    value={createForm.minutes_spent}
                    onChange={e => setCreateForm(d => ({ ...d, minutes_spent: e.target.value }))}
                  />
                </div>
              </div>
              <div className="dts-field">
                <label className="dts-label">Status</label>
                <select
                  className="dts-select"
                  value={createForm.status}
                  onChange={e => setCreateForm(d => ({ ...d, status: e.target.value }))}
                >
                  {STATUS_OPTS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="dts-modal-footer">
              <button className="uv-btn uv-btn--save" onClick={createReport} disabled={creating}>
                {creating ? "Creating…" : "Create Report"}
              </button>
              <button className="uv-btn uv-btn--cancel" onClick={() => setShowCreate(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   TimesheetEntryModal — reusable "Log Time" modal
   Pre-locks ref_type="task" and ref_id=taskId.
   Imported by MasterView for the Effort tab.
───────────────────────────────────────────── */
export function TimesheetEntryModal({ taskId, refType, refId, showToast, onClose, onCreated }) {
  // Support generic ref: if refType/refId are provided, use them; otherwise fall back to task/taskId
  const _refType = refType || "task";
  const _refId   = refId   || taskId;
  const [form, setForm] = useState({
    report_date:   today(),
    title:         "",
    overall_notes: "",
    hours_spent:   0,
    minutes_spent: 0,
    status:        "Draft",
    epic_id:       "",
  });
  const [saving, setSaving] = useState(false);
  const [alertDialog, setAlertDialog] = useState(null);
  // Defect has no epic yet — needs one attached via this entry (see
  // _apply_defect_epic_requirement on the backend, which now rejects EVERY
  // status, not just Submit/Approve). refType/refId are fixed props here
  // (pre-locked, e.g. opened from the Defect Drawer's Time Entry tab), so
  // this only needs to run once per mount, not on every ref change.
  const [defectEpicOptions, setDefectEpicOptions] = useState(null);

  useEffect(() => {
    if (_refType !== "defect" || !_refId) {
      setDefectEpicOptions(null);
      return;
    }
    let cancelled = false;
    fetch(`${API}/defects/${_refId}`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(defect => {
        if (cancelled || !defect) return;
        if (defect.epic_id) {
          setDefectEpicOptions(null);
          return;
        }
        if (!defect.project_id) return;
        return fetch(`${API}/epics?project_id=${defect.project_id}&include_closed=false`, { credentials: "include" })
          .then(r => r.ok ? r.json() : [])
          .then(data => {
            if (cancelled) return;
            const list = Array.isArray(data) ? data : (data?.items || []);
            setDefectEpicOptions(list);
          });
      })
      .catch(() => { if (!cancelled) setDefectEpicOptions(null); });
    return () => { cancelled = true; };
  }, [_refType, _refId]);

  const openAlert = (type, message, confirmLabel = null, onConfirm = null) => {
    setAlertDialog({ type, message, confirmLabel, onConfirm });
  };
  const closeAlert = () => setAlertDialog(null);

  const submitEntry = async () => {
    setSaving(true);
    try {
      const body = {
        ...form,
        hours_spent:   Number(form.hours_spent),
        minutes_spent: Number(form.minutes_spent),
        ref_type: _refType,
        ref_id:   _refId,
      };
      const r = await fetch(`${API}/daily-reports`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        let detail = "Failed to create timesheet entry";
        try {
          const err = await r.json();
          detail = err?.detail || detail;
        } catch {
          // keep fallback message
        }
        throw new Error(detail);
      }
      showToast("Timesheet entry created");
      onCreated?.();
      onClose();
    } catch (err) {
      openAlert("error", err?.message || "Failed to create timesheet entry");
    }
    setSaving(false);
  };

  const handleSave = async () => {
    if (!_refType || !_refId) { openAlert("warning", "A reference entity is required"); return; }
    if (!form.title.trim()) { openAlert("warning", "Title is required"); return; }
    // No timesheet entry against a defect is allowed without an epic — not
    // even Draft. Mirrors the backend's _apply_defect_epic_requirement.
    if (defectEpicOptions && !form.epic_id) {
      openAlert("warning", "This defect has no epic assigned yet — pick an epic to log time against it.");
      return;
    }
    const guard = validateSideDrawerTimeEntry(form.hours_spent, form.minutes_spent);
    if (!guard.ok) {
      if (guard.kind === "confirm_over_2h") {
        openAlert("warning", guard.msg, "Submit", () => submitEntry());
        return;
      }
      if (guard.msg) openAlert("warning", guard.msg);
      return;
    }
    submitEntry();
  };

  return (
    <div className="dts-overlay" onClick={onClose}>
      <div className="dts-modal" onClick={e => e.stopPropagation()}>
        <div className="dts-modal-header">
          <span className="dts-modal-title">Log Time</span>
          <button className="dts-close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="dts-modal-body">
          <div className="dts-field">
            <label className="dts-label">Date</label>
            <input
              type="date" className="dts-input"
              value={form.report_date}
              onChange={e => setForm(d => ({ ...d, report_date: e.target.value }))}
            />
          </div>
          <div className="dts-field">
            <label className="dts-label">Title *</label>
            <input
              className="dts-input"
              placeholder="e.g. Sprint 12 Day 3"
              value={form.title}
              autoFocus
              onChange={e => setForm(d => ({ ...d, title: e.target.value }))}
              onKeyDown={e => e.key === "Enter" && handleSave()}
            />
          </div>
          <div className="dts-field">
            <label className="dts-label">Overall Notes</label>
            <textarea
              className="dts-textarea"
              rows={3}
              placeholder="Summary of work done…"
              value={form.overall_notes}
              onChange={e => setForm(d => ({ ...d, overall_notes: e.target.value }))}
            />
          </div>
          <div className="dts-modal-row">
            <div className="dts-field">
              <label className="dts-label">Hours Spent</label>
              <input
                type="number" className="dts-input"
                min={0} max={24}
                value={form.hours_spent}
                onChange={e => setForm(d => ({ ...d, hours_spent: e.target.value }))}
              />
            </div>
            <div className="dts-field">
              <label className="dts-label">Minutes Spent</label>
              <input
                type="number" className="dts-input"
                min={0} max={59}
                value={form.minutes_spent}
                onChange={e => setForm(d => ({ ...d, minutes_spent: e.target.value }))}
              />
            </div>
          </div>
          {defectEpicOptions && (
            <div className="dts-field">
              <label className="dts-label">Epic *</label>
              <select
                className="dts-select"
                value={form.epic_id}
                onChange={e => setForm(d => ({ ...d, epic_id: e.target.value }))}
              >
                <option value="">This defect has no epic — pick one to log time against it</option>
                {defectEpicOptions.map(ep => (
                  <option key={ep.id} value={ep.id}>{ep.epic_name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="dts-field">
            <label className="dts-label">Status</label>
            <select
              className="dts-select"
              value={form.status}
              onChange={e => setForm(d => ({ ...d, status: e.target.value }))}
            >
              {STATUS_OPTS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div className="dts-modal-footer">
          <button className="uv-btn uv-btn--save" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save Entry"}
          </button>
          <button className="uv-btn uv-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
        </div>
      </div>

      {alertDialog && (
        <div className="dts-alert-overlay" onClick={closeAlert}>
          <div className="dts-alert-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dts-alert-icon" aria-hidden="true">⚠</div>
            <div className="dts-alert-title">
              {alertDialog.type === "error" ? "Error" : "Warning"}
            </div>
            <div className="dts-alert-message">{alertDialog.message}</div>
            <div className="dts-alert-actions">
              {alertDialog.confirmLabel && (
                <button
                  className="uv-btn uv-btn--save"
                  onClick={() => {
                    const fn = alertDialog.onConfirm;
                    closeAlert();
                    fn?.();
                  }}
                >
                  {alertDialog.confirmLabel}
                </button>
              )}
              <button className="uv-btn uv-btn--cancel" onClick={closeAlert}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
